/**
 * Complete Profile Page
 *
 * Custom form for registered users to complete their instructor profile
 * Sends data directly to API instead of using HubSpot embedded forms
 */

import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import { useAuth } from '../../hooks/useAuth';
import { FileUpload } from '../../components/common/FileUpload';
import { LocationSelector } from '../../components/common/LocationSelector';
import { profileService, ProfileFormData, VALID_SPECIALTIES } from '../../services/profileService';

export function CompleteProfile() {
  const { user } = useAuth();

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>({
    firstname: '',
    lastname: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    address: '',
    escuela_de_formacion: '',
    ano_de_formacion: '',
    maestro_de_la_formacion: '',
    desarrollo_personal: false,
    specialties: [],
    // Materials DAI
    has_crystals_bed: false,
    has_arrow: false,
    has_steel_bars: false,
    has_concrete_blocks: false,
    has_other: false,
    complemento_material_dai: '',
    // Suppliers
    provides_wood: false,
    provides_tepe: false,
    provides_iron_bar: false,
    provides_arrows: false,
    provides_wood_board: false,
    provides_concrete_block: false,
    // Experience
    nivel_de_experiencia_en_fuego: '',
    cuentanos_tu_experiencia: '',
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [certificadoFile, setCertificadoFile] = useState<File | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (!user) {
    return null;
  }

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value, type } = target;

    if (type === 'checkbox') {
      const checked = (target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle specialty checkbox changes (multi-select)
  const handleSpecialtyChange = (specialty: string, checked: boolean) => {
    setFormData(prev => {
      const currentSpecialties = prev.specialties || [];
      if (checked) {
        // Add specialty if not already present
        if (!currentSpecialties.includes(specialty)) {
          return { ...prev, specialties: [...currentSpecialties, specialty] };
        }
      } else {
        // Remove specialty
        return { ...prev, specialties: currentSpecialties.filter(s => s !== specialty) };
      }
      return prev;
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.firstname?.trim()) errors.firstname = 'El nombre es obligatorio';
    if (!formData.lastname?.trim()) errors.lastname = 'Los apellidos son obligatorios';
    if (!formData.country?.trim()) errors.country = 'El país es obligatorio';
    if (!formData.state?.trim()) errors.state = 'La provincia/estado es obligatoria';
    if (!formData.city?.trim()) errors.city = 'La ciudad es obligatoria';
    if (!formData.escuela_de_formacion?.trim()) errors.escuela_de_formacion = 'La escuela de certificación es obligatoria';

    // Year validation is simplified since select dropdown only allows valid values
    // Just ensure a year was selected (not the placeholder option)
    if (!formData.ano_de_formacion?.trim()) {
      errors.ano_de_formacion = 'El año de certificación es obligatorio';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    setSubmitError(null);

    // Validate form
    if (!validateForm()) {
      setSubmitError('Por favor, completa todos los campos obligatorios');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Add files to form data
      const submitData: ProfileFormData = {
        ...formData,
        foto: photoFile || undefined,
        certificado: certificadoFile || undefined,
      };

      console.log('[CompleteProfile] Submitting profile with files:', {
        hasPhoto: !!photoFile,
        photoName: photoFile?.name,
        photoSize: photoFile?.size,
        hasCertificate: !!certificadoFile,
        certificateName: certificadoFile?.name,
        certificateSize: certificadoFile?.size
      });

      const response = await profileService.submitProfile(submitData);

      if (response.success) {
        // Redirect to pending approval page
        route('/esperando-aprobacion');
      } else {
        setSubmitError(response.error || 'Error al enviar el perfil');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Error al enviar el perfil');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div class="max-w-4xl mx-auto">
      {/* Header - texto directo sin fondo */}
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900">Completa tu Perfil de Instructor</h1>
        <p class="mt-2 text-sm text-gray-600">
          Por favor, completa el siguiente formulario para solicitar tu registro como instructor certificado.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} class="space-y-6">

            {/* Error Alert */}
            {submitError && (
              <div class="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-red-700">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Section 1: Basic Information */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Datos Básicos</h2>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstname" class="block text-sm font-medium text-gray-700">
                    Nombre <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstname"
                    name="firstname"
                    value={formData.firstname}
                    onInput={handleInputChange}
                    required
                    class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      fieldErrors.firstname
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {fieldErrors.firstname && (
                    <p class="mt-1 text-sm text-red-600">{fieldErrors.firstname}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastname" class="block text-sm font-medium text-gray-700">
                    Apellidos <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastname"
                    name="lastname"
                    value={formData.lastname}
                    onInput={handleInputChange}
                    required
                    class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      fieldErrors.lastname
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {fieldErrors.lastname && (
                    <p class="mt-1 text-sm text-red-600">{fieldErrors.lastname}</p>
                  )}
                </div>

                <div class="sm:col-span-2">
                  <label htmlFor="phone" class="block text-sm font-medium text-gray-700">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onInput={handleInputChange}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Location */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Ubicación</h2>

              <LocationSelector
                countryValue={formData.country}
                stateValue={formData.state}
                cityValue={formData.city}
                onCountryChange={(name) => setFormData(prev => ({
                  ...prev,
                  country: name,
                  state: '',
                  city: ''
                }))}
                onStateChange={(name) => setFormData(prev => ({
                  ...prev,
                  state: name,
                  city: ''
                }))}
                onCityChange={(name) => setFormData(prev => ({
                  ...prev,
                  city: name
                }))}
                errors={{
                  country: fieldErrors.country,
                  state: fieldErrors.state,
                  city: fieldErrors.city
                }}
                required={{
                  country: true,
                  state: true,
                  city: true
                }}
              />

              {/* Address input remains as text */}
              <div class="mt-6">
                <label htmlFor="address" class="block text-sm font-medium text-gray-700">
                  Dirección (privada)
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onInput={handleInputChange}
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Section 3: Certification */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Certificación</h2>
              <div class="space-y-6">
                <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="escuela_de_formacion" class="block text-sm font-medium text-gray-700">
                      Escuela de Certificación <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="escuela_de_formacion"
                      name="escuela_de_formacion"
                      value={formData.escuela_de_formacion}
                      onChange={handleInputChange}
                      required
                      class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        fieldErrors.escuela_de_formacion
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Selecciona una escuela</option>
                      <option value="InnerFire">InnerFire</option>
                      <option value="The FIT">The FIT</option>
                      <option value="FIRE">FIRE</option>
                      <option value="Firewalking American Institute">Firewalking American Institute</option>
                      <option value="Firewalking Center">Firewalking Center</option>
                      <option value="Sudor">Sudor</option>
                    </select>
                    {fieldErrors.escuela_de_formacion && (
                      <p class="mt-1 text-sm text-red-600">{fieldErrors.escuela_de_formacion}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ano_de_formacion" class="block text-sm font-medium text-gray-700">
                      Año de Certificación <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="ano_de_formacion"
                      name="ano_de_formacion"
                      value={formData.ano_de_formacion}
                      onChange={handleInputChange}
                      required
                      class={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        fieldErrors.ano_de_formacion
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    >
                      <option value="">Selecciona el año</option>
                      {Array.from(
                        { length: new Date().getFullYear() - 1950 + 1 },
                        (_, i) => new Date().getFullYear() - i
                      ).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    {fieldErrors.ano_de_formacion && (
                      <p class="mt-1 text-sm text-red-600">{fieldErrors.ano_de_formacion}</p>
                    )}
                  </div>

                  <div class="sm:col-span-2">
                    <label htmlFor="maestro_de_la_formacion" class="block text-sm font-medium text-gray-700">
                      Maestro/Instructor que Certificó
                    </label>
                    <input
                      type="text"
                      id="maestro_de_la_formacion"
                      name="maestro_de_la_formacion"
                      value={formData.maestro_de_la_formacion}
                      onInput={handleInputChange}
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* File uploads */}
                <div class="space-y-6">
                  <FileUpload
                    id="instructor_photo"
                    label="Foto de Perfil"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    maxSize={2}
                    helpText="Formato: JPG, PNG o WEBP. Máximo 2MB"
                    onFileSelect={setPhotoFile}
                    fileType="image"
                  />

                  <FileUpload
                    id="certificado"
                    label="Certificado"
                    accept="application/pdf,image/jpeg,image/jpg,image/png"
                    maxSize={5}
                    helpText="Formato: PDF, JPG o PNG. Máximo 5MB"
                    onFileSelect={setCertificadoFile}
                    fileType="document"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Professional Development */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Desarrollo Personal y Profesional</h2>
              <div class="space-y-6">
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="desarrollo_personal"
                      name="desarrollo_personal"
                      type="checkbox"
                      checked={formData.desarrollo_personal}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="desarrollo_personal" class="font-medium text-gray-700">
                      ¿Eres profesional del Desarrollo Personal?
                    </label>
                  </div>
                </div>

                {formData.desarrollo_personal && (
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-3">
                      Especialidades
                    </label>
                    <div class="space-y-2">
                      {VALID_SPECIALTIES.map((specialty) => (
                        <div key={specialty} class="flex items-start">
                          <div class="flex items-center h-5">
                            <input
                              id={`specialty_${specialty}`}
                              type="checkbox"
                              checked={formData.specialties?.includes(specialty) || false}
                              onChange={(e) => handleSpecialtyChange(specialty, (e.target as HTMLInputElement).checked)}
                              class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </div>
                          <div class="ml-3 text-sm">
                            <label htmlFor={`specialty_${specialty}`} class="text-gray-700">
                              {specialty}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 5: DAI Materials */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Material DAI</h2>
              <p class="text-sm text-gray-600 mb-4">
                Selecciona los materiales DAI que tienes disponibles:
              </p>
              <div class="space-y-4">
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="has_crystals_bed"
                      name="has_crystals_bed"
                      type="checkbox"
                      checked={formData.has_crystals_bed}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="has_crystals_bed" class="font-medium text-gray-700">
                      Cama de Cristales
                    </label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="has_arrow"
                      name="has_arrow"
                      type="checkbox"
                      checked={formData.has_arrow}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="has_arrow" class="font-medium text-gray-700">
                      Flechas
                    </label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="has_steel_bars"
                      name="has_steel_bars"
                      type="checkbox"
                      checked={formData.has_steel_bars}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="has_steel_bars" class="font-medium text-gray-700">
                      Barras de Acero
                    </label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="has_concrete_blocks"
                      name="has_concrete_blocks"
                      type="checkbox"
                      checked={formData.has_concrete_blocks}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="has_concrete_blocks" class="font-medium text-gray-700">
                      Bloques de Hormigón
                    </label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="has_other"
                      name="has_other"
                      type="checkbox"
                      checked={formData.has_other}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="has_other" class="font-medium text-gray-700">
                      Otro Material
                    </label>
                  </div>
                </div>

                {formData.has_other && (
                  <div class="ml-7">
                    <label htmlFor="complemento_material_dai" class="block text-sm font-medium text-gray-700">
                      Especifica el material
                    </label>
                    <input
                      type="text"
                      id="complemento_material_dai"
                      name="complemento_material_dai"
                      value={formData.complemento_material_dai}
                      onInput={handleInputChange}
                      placeholder="Describe el otro material DAI que tienes..."
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section 6: Supplier */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Proveedor de Materiales</h2>
              <p class="text-sm text-gray-600 mb-4">
                Marca los materiales que puedes proveer a otros instructores:
              </p>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_wood"
                      name="provides_wood"
                      type="checkbox"
                      checked={formData.provides_wood}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_wood" class="font-medium text-gray-700">Leña</label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_tepe"
                      name="provides_tepe"
                      type="checkbox"
                      checked={formData.provides_tepe}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_tepe" class="font-medium text-gray-700">Tepe</label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_iron_bar"
                      name="provides_iron_bar"
                      type="checkbox"
                      checked={formData.provides_iron_bar}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_iron_bar" class="font-medium text-gray-700">Barra de Hierro</label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_arrows"
                      name="provides_arrows"
                      type="checkbox"
                      checked={formData.provides_arrows}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_arrows" class="font-medium text-gray-700">Flechas</label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_wood_board"
                      name="provides_wood_board"
                      type="checkbox"
                      checked={formData.provides_wood_board}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_wood_board" class="font-medium text-gray-700">Tabla de Madera</label>
                  </div>
                </div>

                <div class="flex items-start">
                  <div class="flex items-center h-5">
                    <input
                      id="provides_concrete_block"
                      name="provides_concrete_block"
                      type="checkbox"
                      checked={formData.provides_concrete_block}
                      onChange={handleInputChange}
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label htmlFor="provides_concrete_block" class="font-medium text-gray-700">Bloque de Hormigón</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Experience */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Experiencia</h2>
              <div class="space-y-6">
                <div>
                  <label htmlFor="nivel_de_experiencia_en_fuego" class="block text-sm font-medium text-gray-700">
                    Experiencia con Fuego
                  </label>
                  <select
                    id="nivel_de_experiencia_en_fuego"
                    name="nivel_de_experiencia_en_fuego"
                    value={formData.nivel_de_experiencia_en_fuego}
                    onChange={handleInputChange}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="Cerilla">Cerilla</option>
                    <option value="Brasero">Brasero</option>
                    <option value="Horno">Horno</option>
                    <option value="Vulcano">Vulcano</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="cuentanos_tu_experiencia" class="block text-sm font-medium text-gray-700">
                    Cuéntanos tu Experiencia
                  </label>
                  <textarea
                    id="cuentanos_tu_experiencia"
                    name="cuentanos_tu_experiencia"
                    rows={5}
                    value={formData.cuentanos_tu_experiencia}
                    onInput={handleInputChange}
                    placeholder="Comparte tu experiencia como instructor de Firewalking y DAI..."
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Submit button */}
            <div class="bg-white shadow sm:rounded-lg p-6">
              <div class="flex items-center justify-between">
                <div class="text-sm text-gray-600">
                  <span class="text-red-500">*</span> Campos obligatorios
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  class={`
                    inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white
                    ${isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    }
                  `}
                >
                  {isSubmitting ? (
                    <>
                      <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Perfil'
                  )}
                </button>
              </div>

              {/* Info message */}
              <div class="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-blue-700">
                      Una vez enviado el formulario, tu solicitud será revisada por nuestro equipo.
                      Recibirás una notificación por email con el resultado.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </form>
    </div>
  );
}
